import { z } from "zod";
import {
  ContentFitnessCoverageStatusSchema,
  ContentFitnessVerificationStateSchema,
} from "../../enums/content-fitness";
import { EvidenceKindSchema } from "../../enums/yeet";
import { MetricKeySchema } from "../../report/catalog";
import { ContentFitnessSubcriterionKeySchema } from "../../report/content-fitness/subcriteria";
import { IssueSchema } from "../issue/responses";
import { JobStatusValueSchema } from "../job/responses";
import { ScanScheduleSchema } from "../website/responses";

// JSON mirror of DashboardData (backend dashboard.service). Timestamps cross the
// wire as ISO strings; z.coerce.date() restores Date so the UI is typed safely.
/**
 * All persisted Content Fitness subcriterion assessments for one dimension in
 * the website's latest completed run, mapped to page ids. Replaces the
 * per-page detail fan-out the dimension view previously performed.
 */
export const ContentFitnessDimensionResponseSchema = z.object({
  dimensionKey: MetricKeySchema,
  assessments: z.array(
    z.object({
      pageId: z.uuid().nullable(),
      url: z.string(),
      subcriterionKey: ContentFitnessSubcriterionKeySchema,
      parentMetricKey: MetricKeySchema,
      label: z.string(),
      verificationState: ContentFitnessVerificationStateSchema,
      score: z.number().int().min(0).max(4).nullable(),
      rationale: z.string(),
      evidence: z.array(
        z.object({ kind: EvidenceKindSchema, value: z.string() }),
      ),
    }),
  ),
});

export type ContentFitnessDimensionResponse = z.infer<
  typeof ContentFitnessDimensionResponseSchema
>;

export const OverviewResponseSchema = z.object({
  site: z.object({
    websiteId: z.uuid(),
    clientId: z.uuid(),
    clientName: z.string(),
    reviewBrandTerms: z.array(z.string()),
    displayName: z.string().nullable(),
    title: z.string().nullable(),
    domain: z.string(),
    originUrl: z.string(),
    categoryLabel: z.string().nullable(),
  }),
  scanSchedule: ScanScheduleSchema,
  signal: z.object({
    score: z.number().nullable(),
    scoreDelta: z.number().nullable(),
    trend: z.array(z.number()),
    createdAt: z.coerce.date().nullable(),
  }),
  benchmark: z
    .object({
      target: z.number().nullable(),
      peerMedian: z.number().nullable(),
      percentile: z.number().nullable(),
      distribution: z.array(z.number()),
    })
    .nullable(),
  runSummary: z
    .object({
      checks: z.number().nullable(),
      newIssues: z.number().nullable(),
      resolvedIssues: z.number().nullable(),
      triggerLabel: z.string().nullable(),
    })
    .nullable(),
  alertQuality: z
    .object({
      usefulPercent: z.number().nullable(),
      falsePositivePercent: z.number().nullable(),
      improvedPercent: z.number().nullable(),
      alertsFired: z.number().nullable(),
      windowLabel: z.string().nullable(),
    })
    .nullable(),
  changes: z.array(
    z.object({
      metricKey: MetricKeySchema,
      delta: z.number(),
      note: z.string(),
    }),
  ),
  signals: z.array(
    z.object({
      key: MetricKeySchema,
      score: z.number(),
      delta: z.number(),
      detail: z.string(),
      trend: z.array(z.number()),
      // Content Fitness pilot coverage for this dimension, aggregated across
      // the report's pages. Null on non-Content-Fitness signals. Structured so
      // the UI never re-parses coverage out of `detail`.
      coverage: z
        .object({
          coverageStatus: ContentFitnessCoverageStatusSchema,
          assessedSubcriteria: z.number().int().min(0),
          canonicalSubcriteria: z.number().int().min(0),
          capturedPages: z.number().int().min(0),
          assessedPages: z.number().int().min(0),
          scoredPages: z.number().int().min(0),
        })
        .nullable(),
    }),
  ),
  pages: z.array(
    z.object({
      id: z.uuid(),
      title: z.string().nullable(),
      path: z.string(),
      contentFitness: z
        .object({
          score: z.number().int().min(0).max(100).nullable(),
          coverageStatus: ContentFitnessCoverageStatusSchema,
          scoredSubcriteria: z.number().int().min(0),
          canonicalSubcriteria: z.number().int().min(0),
        })
        .nullable(),
      humanScore: z.number().nullable(),
      agentScore: z.number().nullable(),
      mapX: z.number().nullable(),
      mapY: z.number().nullable(),
    }),
  ),
  links: z.array(
    z.object({
      fromPageId: z.uuid(),
      toPageId: z.uuid(),
      agentBroken: z.boolean(),
      brokenReason: z.string().nullable(),
    }),
  ),
  issues: z.array(IssueSchema),
  lastRun: z
    .object({
      id: z.uuid(),
      status: JobStatusValueSchema,
      startedAt: z.coerce.date().nullable(),
      finishedAt: z.coerce.date().nullable(),
      pageCount: z.number().int(),
      contentFitnessCoverage: z.object({
        capturedPages: z.number().int().min(0),
        assessedPages: z.number().int().min(0),
        scoredPages: z.number().int().min(0),
      }),
    })
    .nullable(),
  latestAttempt: z
    .object({
      id: z.uuid(),
      status: JobStatusValueSchema,
      startedAt: z.coerce.date().nullable(),
      finishedAt: z.coerce.date().nullable(),
    })
    .nullable(),
});

export type OverviewResponse = z.infer<typeof OverviewResponseSchema>;
